import React from "react";
import { observer } from "mobx-react";
import { PublisherCreateStore } from "../../store";
import { PublisherCreateForm } from "./PublisherCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { PersonType } from "../../../../api/graphql";
import { MaritalStatus } from "../../../../api/graphql";

export type PublisherCreateProps = {
  publisherCreateStore: PublisherCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const PublisherCreate: React.FC<PublisherCreateProps> = observer(
  ({ publisherCreateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={publisherCreateStore.isSaving} />
        <PublisherCreateForm
          publisherCreateStore={publisherCreateStore}
          personTypeError={publisherCreateStore.errors.personType?.message}
          personTypeOptions={publisherCreateStore.getPersonTypeOptions()}
          onPersonTypeChange={(value: PersonType) => {
            publisherCreateStore.setPersonTypeValue(value);
          }}
          personType={publisherCreateStore.entityValues.personType}
          nameError={publisherCreateStore.errors.name?.message}
          onNameChange={(value: string) => {
            publisherCreateStore.setNameValue(value);
          }}
          name={publisherCreateStore.entityValues.name}
          maritalStatusError={
            publisherCreateStore.errors.maritalStatus?.message
          }
          maritalStatusOptions={publisherCreateStore.getMaritalStatusOptions()}
          onMaritalStatusChange={(value: MaritalStatus | "") => {
            publisherCreateStore.setMaritalStatusValue(value);
          }}
          maritalStatus={publisherCreateStore.entityValues.maritalStatus}
          nationalityError={publisherCreateStore.errors.nationality?.message}
          onNationalityChange={(value) => {
            publisherCreateStore.setNationalityValue(value);
          }}
          nationalitySelectedValue={
            publisherCreateStore.entityValues.showNationality
          }
          onNationalitySelected={(value) => {
            publisherCreateStore.setSelectedNationality(value);
          }}
          nationalityOptions={publisherCreateStore.getNationalityOptions()}
          pseudonymError={publisherCreateStore.errors.pseudonym?.message}
          onPseudonymChange={(value: string) => {
            publisherCreateStore.setPseudonymValue(value);
          }}
          pseudonym={publisherCreateStore.entityValues.pseudonym}
          taxIdError={publisherCreateStore.errors.taxId?.message}
          onTaxIdChange={(value: string) => {
            publisherCreateStore.setTaxIdValue(value);
          }}
          taxId={publisherCreateStore.entityValues.taxId}
          phoneError={publisherCreateStore.errors.phone?.message}
          onPhoneChange={(value: string) => {
            publisherCreateStore.setPhoneValue(value);
          }}
          phone={publisherCreateStore.entityValues.phone}
          emailError={publisherCreateStore.errors.email?.message}
          onEmailChange={(value: string) => {
            publisherCreateStore.setEmailValue(value);
          }}
          email={publisherCreateStore.entityValues.email}
          streetAndNumberError={
            publisherCreateStore.errors.streetAndNumber?.message
          }
          onStreetAndNumberChange={(value: string) => {
            publisherCreateStore.setStreetAndNumberValue(value);
          }}
          streetAndNumber={publisherCreateStore.entityValues.streetAndNumber}
          floorError={publisherCreateStore.errors.floor?.message}
          onFloorChange={(value: string) => {
            publisherCreateStore.setFloorValue(value);
          }}
          floor={publisherCreateStore.entityValues.floor}
          suiteError={publisherCreateStore.errors.suite?.message}
          onSuiteChange={(value: string) => {
            publisherCreateStore.setSuiteValue(value);
          }}
          suite={publisherCreateStore.entityValues.suite}
          postalCodeError={publisherCreateStore.errors.postalCode?.message}
          onPostalCodeChange={(value: string) => {
            publisherCreateStore.setPostalCodeValue(value);
          }}
          postalCode={publisherCreateStore.entityValues.postalCode}
          countryError={publisherCreateStore.errors.country?.message}
          onCountryChange={(value) => {
            publisherCreateStore.setCountryValue(value);
          }}
          countrySelectedValue={publisherCreateStore.entityValues.showCountry}
          onCountrySelected={(value) => {
            publisherCreateStore.setSelectedCountry(value);
          }}
          countryOptions={publisherCreateStore.getCountryOptions()}
          provinceError={publisherCreateStore.errors.province?.message}
          onProvinceChange={(value) => {
            publisherCreateStore.setProvinceValue(value);
          }}
          provinceSelectedValue={publisherCreateStore.entityValues.showProvince}
          onProvinceSelected={(value) => {
            publisherCreateStore.setSelectedProvince(value);
          }}
          provinceOptions={publisherCreateStore.getProvinceOptions()}
          cityError={publisherCreateStore.errors.city?.message}
          onCityChange={(value: string) => {
            publisherCreateStore.setCityValue(value);
          }}
          city={publisherCreateStore.entityValues.city}
          caeIpiError={publisherCreateStore.errors.caeIpi?.message}
          onCaeIpiChange={(value: string) => {
            publisherCreateStore.setCaeIpiValue(value);
          }}
          caeIpi={publisherCreateStore.entityValues.caeIpi}
          publisherRepresentativeCreateStore={
            publisherCreateStore.representativesCreateStore
          }
          representativesVisibleColumns={
            publisherCreateStore.representativesVisibleColumns
          }
          representativesOnRestore={(id: string) =>
            publisherCreateStore.representativesRestore(id)
          }
          representativesOnCommitChanges={(changes) =>
            publisherCreateStore.representativesCommitChanges(changes)
          }
          representativesValidateChanges={(changes) =>
            publisherCreateStore.representativesValidateChanges(changes)
          }
          representativesRows={
            publisherCreateStore.entityValues.showRepresentatives
          }
          representativesError={
            publisherCreateStore.errors.representatives?.message
          }
          publisherCollectingSocietyCreateStore={
            publisherCreateStore.managementSocietiesCreateStore
          }
          managementSocietiesVisibleColumns={
            publisherCreateStore.managementSocietiesVisibleColumns
          }
          managementSocietiesOnRestore={(id: string) =>
            publisherCreateStore.managementSocietiesRestore(id)
          }
          managementSocietiesOnCommitChanges={(changes) =>
            publisherCreateStore.managementSocietiesCommitChanges(changes)
          }
          managementSocietiesValidateChanges={(changes) =>
            publisherCreateStore.managementSocietiesValidateChanges(changes)
          }
          managementSocietiesRows={
            publisherCreateStore.entityValues.showManagementSocieties
          }
          managementSocietiesError={
            publisherCreateStore.errors.managementSocieties?.message
          }
          dummyCRUDRestoredObservable={
            publisherCreateStore.dummyCRUDRestoredObservable
          }
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={publisherCreateStore.isProcessing}
          onSave={() => publisherCreateStore.saveNewPublisher()}
          onCancel={() => publisherCreateStore.cancelNewPublisher()}
        />
        <ErrorDialog
          open={publisherCreateStore.errorDialogOpened}
          title={publisherCreateStore.errorTitle}
          description={publisherCreateStore.errorDescription}
          onClose={() => publisherCreateStore.closeErrorDialog()}
        />
      </>
    );
  }
);
